import React from 'react';
import { StepperV2, type StepperV2Step } from '@lmn/design-system';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { useStepKeys } from '@/app/components/hotel-detail/evo/EvoBreadcrumbs';
import { useTranslation } from '@/app/components/translations/Translation';

/**
 * MPH-1452: whether the Evo hotel detail and transport pages show the design-system StepperV2 instead of
 * EvoBreadcrumbs (and the "Your trip" modal drops its step list). Off by default; forced with
 * force_config_evo_stepper_v2_enabled=true.
 */
export function useEvoStepperV2Enabled() {
  const { localization } = usePageWrapperData();
  return !!localization?.configValuesApp?.evoStepperV2Enabled;
}

type EvoStepperProps = Omit<React.HTMLAttributes<HTMLElement>, 'children' | 'onClick'> & {
  /** `horizontal` is the desktop row, `compact` the mobile bar that opens a bottom sheet with every step. */
  layout: 'horizontal' | 'compact';
  /** 1-based, like EvoBreadcrumbs: 1 is "Choose Stay". */
  activeStep: number;
  /** Called with the 1-based number of a completed step when it is chosen. Completed steps are buttons only when provided. */
  onStepClick?: (stepNumber: number) => void;
};

/**
 * The Evo funnel steps rendered with the design-system StepperV2 (Figma Stepper-DS). The steps come from
 * useStepKeys, like EvoBreadcrumbs, so there are four of them, three on Hotel Only, and step 2 says "Train"
 * on T+H. StepperV2 counts from 0, so the 1-based step numbers are converted on the way in and out.
 * Other HTML attributes, `className` included, reach the root `<nav>`, so a layout can place it with
 * `style={{ gridArea: 'stepper' }}` and style it with the `css` prop.
 */
export function EvoStepper({ layout, activeStep, onStepClick, ...props }: EvoStepperProps) {
  const { t } = useTranslation();
  const stepKeys = useStepKeys();

  // StepperV2 only makes completed steps interactive, which keeps EvoBreadcrumbs' rule.
  const steps: StepperV2Step[] = stepKeys.map((stepKey) => ({
    label: t(stepKey),
    onClick: onStepClick ? ({ currentStep }) => onStepClick(currentStep + 1) : undefined,
  }));

  return (
    <StepperV2
      {...props}
      layout={layout}
      steps={steps}
      activeStep={activeStep - 1}
      closeSheetLabel={t('vacation.select.details.close') || undefined}
      // t() returns '' for a key missing from the dictionary; the fallbacks are StepperV2's English defaults.
      getStepCounterLabel={(stepNumber, totalSteps) =>
        t('vacation.select.cc.nav.step_counter', { current: String(stepNumber), total: String(totalSteps) }) ||
        `Step ${stepNumber} of ${totalSteps}`
      }
      completedStepLabel={t('vacation.select.cc.nav.step_completed') || undefined}
      aria-label={t('vacation.select.cc.nav.progress') || undefined}
    />
  );
}
